import { useEffect } from 'react'
import { Outlet, useNavigate, useLocation } from 'react-router-dom'
import { useStores } from '../../../hooks'
import { Chip, NavItem, NavSection } from '@clickguard/design-system'
import { CreditCardExpiryBanner } from '../../../components/credit-card-expiry-banner'
import { useTrackPostHogEvent } from '../../../utils/posthog'
import { PostHogEvent, PostHogPath } from '../../../utils/posthog-events'
import { useFeatureFlag } from 'services/feature-flags'
import { useWorkspaceAccess } from 'hooks/use-workspace-access'

export const Settings = () => {
	const {
		assetStore,
		accountStore,
		billingStore: { offers }
	} = useStores()
	const { asset, demoMode } = assetStore
	const { account, setWantsToSeeDemoData } = accountStore
	const trackPostHogEvent = useTrackPostHogEvent()
	const navigate = useNavigate()
	const { pathname } = useLocation()
	const isServerSideTrackingEnabled = useFeatureFlag(
		'server_side_tracking_enabled'
	)
	const accountId = demoMode && !account?.id ? 'workspace-demo' : account?.id
	const { access } = useWorkspaceAccess()
	const assetId = demoMode && !asset?.id ? 'website-demo' : asset?.id
	const isOffersAvailable = offers.length > 0
	const paymentMethodStatus = account?.billing?.payment_method?.status
	const subscriptionStatus = account?.billing?.subscription?.status
	const shouldShowCcExpiryBanner =
		paymentMethodStatus === 'EXPIRING_SOON' &&
		subscriptionStatus === 'ACTIVE'

	const isActive = (path: string) => pathname === path
	const buildNavItemClass = (active: boolean) =>
		`!text-[12px] !leading-4 ${active ? '!font-semibold !bg-[color:var(--lavender-fill-soft,#eeedff)]' : '!font-normal'}`

	useEffect(() => {
		if (!account?.id) {
			setWantsToSeeDemoData(true)
		} else {
			setWantsToSeeDemoData(false)
		}
		return () => {
			setWantsToSeeDemoData(false)
		}
	}, [account])

	const workspaceDisabled = accountId === 'workspace-demo'
	// Cases 2 (no trial yet) and 3 (no ad account) turn the website pages off;
	// Case 6 (cancelled) keeps Connections. Case 2 also loses Billing (it has no
	// subscription) and Offers.
	const websiteDisabled =
		!['full', 'cancelled'].includes(access) ||
		accountId === 'workspace-demo' ||
		!account?.billing?.subscription ||
		assetId === 'website-demo'

	return (
		<div className="flex gap-4 p-6">
			<nav className="flex flex-col gap-3.5 bg-white border border-t-traffic rounded-xl shadow-[0px_4px_16px_0px_rgba(16,24,40,0.03),0px_1px_4px_0px_rgba(16,24,40,0.02)] w-[220px] shrink-0 p-4 sticky top-6 self-start overflow-y-auto max-h-[calc(100vh-120px)]">
				<NavItem
					size="sm"
					label="Settings"
					className="!font-medium !text-[14px] !leading-[18px] !min-h-0 !px-0.5 !py-0 pointer-events-none"
				/>
				<NavSection title="Workspace">
					<NavItem
						size="sm"
						className={buildNavItemClass(isActive(`/settings/workspace/${accountId}/preferences`))}
						label="Preferences"
						disabled={workspaceDisabled}
						selected={isActive(`/settings/workspace/${accountId}/preferences`)}
						onClick={() => navigate(`/settings/workspace/${accountId}/preferences`)}
					/>
					<NavItem
						size="sm"
						className={buildNavItemClass(isActive(`/settings/workspace/${accountId}/team-access`))}
						label="Team Access"
						disabled={workspaceDisabled}
						selected={isActive(`/settings/workspace/${accountId}/team-access`)}
						onClick={() => navigate(`/settings/workspace/${accountId}/team-access`)}
					/>
					<NavItem
						size="sm"
						className={buildNavItemClass(isActive(`/settings/workspace/${accountId}/billing`))}
						label="Billing"
						disabled={workspaceDisabled || !account?.billing?.subscription}
						selected={isActive(`/settings/workspace/${accountId}/billing`)}
						onClick={() => navigate(`/settings/workspace/${accountId}/billing`)}
					/>
				</NavSection>
				<NavSection title="Website">
					<NavItem
						size="sm"
						className={buildNavItemClass(isActive(`/settings/workspace/${accountId}/asset/${assetId}/site-preference`))}
						label="Preferences"
						disabled={
							!['full', 'cancelled', 'no-ad-account'].includes(access) ||
							assetId === 'website-demo'
						}
						selected={isActive(
							`/settings/workspace/${accountId}/asset/${assetId}/site-preference`
						)}
						onClick={() =>
							navigate(
								`/settings/workspace/${accountId}/asset/${assetId}/site-preference`
							)
						}
					/>
					<NavItem
						size="sm"
						className={buildNavItemClass(isActive(`/settings/workspace/${accountId}/asset/${assetId}/connections`))}
						label="Connections"
						disabled={
							access === 'cancelled'
								? accountId === 'workspace-demo' || assetId === 'website-demo'
								: websiteDisabled
						}
						selected={isActive(
							`/settings/workspace/${accountId}/asset/${assetId}/connections`
						)}
						onClick={() =>
							navigate(
								`/settings/workspace/${accountId}/asset/${assetId}/connections`
							)
						}
					/>
					<NavItem
						size="sm"
						className={buildNavItemClass(isActive(`/settings/workspace/${accountId}/asset/${assetId}/tracking-code`))}
						label="Tracking code"
						disabled={websiteDisabled}
						selected={isActive(`/settings/workspace/${accountId}/asset/${assetId}/tracking-code`)}
						onClick={() => navigate(`/settings/workspace/${accountId}/asset/${assetId}/tracking-code`)}
					/>
					{isServerSideTrackingEnabled && (
						<NavItem
							size="sm"
							className={buildNavItemClass(isActive(`/settings/workspace/${accountId}/asset/${assetId}/conversion-tracking`))}
							label="Conversion tracking"
							disabled={websiteDisabled}
							selected={isActive(`/settings/workspace/${accountId}/asset/${assetId}/conversion-tracking`)}
							onClick={() => navigate(`/settings/workspace/${accountId}/asset/${assetId}/conversion-tracking`)}
						/>
					)}
				</NavSection>
				<NavSection title="Personal">
					<NavItem
						size="sm"
						className={buildNavItemClass(isActive('/settings/user/offers'))}
						label={
							<span className="flex gap-1.5 items-center">
								Offers
								{isOffersAvailable && (
									<Chip size="sm" color="brand">
										New offer
									</Chip>
								)}
							</span>
						}
						disabled={access === 'not-started'}
						selected={isActive('/settings/user/offers')}
						onClick={() => navigate('/settings/user/offers')}
					/>
					<NavItem
						size="sm"
						className={buildNavItemClass(isActive('/settings/user/profile'))}
						label="Profile"
						selected={isActive('/settings/user/profile')}
						onClick={() => navigate('/settings/user/profile')}
					/>
					<NavItem
						size="sm"
						className={buildNavItemClass(isActive('/settings/user/notification'))}
						label="Notifications"
						selected={isActive('/settings/user/notification')}
						onClick={() => navigate('/settings/user/notification')}
					/>
					<NavItem
						size="sm"
						className={buildNavItemClass(isActive('/settings/user/security'))}
						label="Security"
						selected={isActive('/settings/user/security')}
						onClick={() => {
							trackPostHogEvent(
								PostHogEvent.PAGEVIEW_SECURITY,
								PostHogPath.SETTINGS_SECURITY,
								null
							)
							navigate('/settings/user/security')
						}}
					/>
				</NavSection>
			</nav>
			<div className="flex-1 min-w-0">
				{shouldShowCcExpiryBanner && (
					<div className="px-4 mb-4">
						<CreditCardExpiryBanner disablePadding={true} />
					</div>
				)}
				<Outlet context={{ accountId: accountId, assetId: assetId }} />
			</div>
		</div>
	)
}
